/**
 * Public-facing commute survey form. No auth required.
 * Employees open a shared link /commute-survey/:token and submit their typical week.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Leaf } from 'lucide-react';
import { getPublicSurvey, submitCommuteResponse } from '../services/commuteSurveyService';

const MODES = [
  { value: 'car', label: 'Car (petrol/diesel/hybrid/EV)', unit: 'km' },
  { value: 'motorbike', label: 'Motorbike', unit: 'km' },
  { value: 'bus', label: 'Bus', unit: 'passenger.km' },
  { value: 'rail', label: 'Train / Underground / Tram', unit: 'passenger.km' },
  { value: 'taxi', label: 'Taxi', unit: 'km' },
  { value: 'ferry', label: 'Ferry', unit: 'passenger.km' },
];

const CAR_SIZES = [
  { value: 'Small car', label: 'Small' },
  { value: 'Medium car', label: 'Medium' },
  { value: 'Large car', label: 'Large' },
  { value: 'Average car', label: 'I\'m not sure' },
];

const CAR_FUELS = [
  { value: 'diesel', label: 'Diesel' },
  { value: 'petrol', label: 'Petrol' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'phev', label: 'Plug-in hybrid' },
  { value: 'bev', label: 'Battery electric (EV)' },
  { value: 'unknown', label: 'Not sure' },
];

const RAIL_TYPES = [
  { value: 'National rail', label: 'National rail' },
  { value: 'International rail', label: 'International rail' },
  { value: 'Light rail and tram', label: 'Light rail / tram' },
  { value: 'London Underground', label: 'Underground / metro' },
];

const BUS_TYPES = [
  { value: 'Average local bus', label: 'Local bus (average)' },
  { value: 'Local bus (not London)', label: 'Local bus (non-London)' },
  { value: 'Local London bus', label: 'Local London bus' },
  { value: 'Coach', label: 'Coach' },
];

const FERRY_TYPES = [
  { value: 'Foot passenger', label: 'Foot passenger' },
  { value: 'Car passenger', label: 'Car passenger' },
  { value: 'Average (all passenger)', label: 'Average' },
];

const newEntry = () => ({
  id: Math.random().toString(36).slice(2, 8),
  mode: 'car',
  one_way_distance: '',
  days_per_week: 5,
  activity_unit: 'km',
  vehicle_type: 'Average car',
  size_class: 'Average car',
  fuel_type: 'unknown',
  home_address: '',
  work_address: '',
  distance_source: 'manual', // 'manual' | 'addresses'
});

export default function CommuteSurveyPublic() {
  const { token } = useParams();
  const [survey, setSurvey] = useState(null);
  const [err, setErr] = useState(null);
  const [entries, setEntries] = useState([newEntry()]);
  const [wfh, setWfh] = useState(0);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [department, setDepartment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    getPublicSurvey(token)
      .then(setSurvey)
      .catch((e) => setErr(e?.response?.data?.detail || 'Survey unavailable'));
  }, [token]);

  const updateEntry = (id, patch) => setEntries((es) => es.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  const removeEntry = (id) => setEntries((es) => es.filter((e) => e.id !== id));
  const addEntry = () => setEntries((es) => [...es, newEntry()]);

  const handleModeChange = (id, mode) => {
    const m = MODES.find((x) => x.value === mode);
    let vehicle_type = null;
    if (mode === 'car') vehicle_type = 'Average car';
    if (mode === 'rail') vehicle_type = 'National rail';
    if (mode === 'bus') vehicle_type = 'Average local bus';
    if (mode === 'ferry') vehicle_type = 'Average (all passenger)';
    if (mode === 'motorbike') vehicle_type = 'Average';
    if (mode === 'taxi') vehicle_type = 'Regular taxi';
    updateEntry(id, {
      mode,
      activity_unit: m?.unit || 'km',
      vehicle_type,
      size_class: mode === 'car' ? 'Average car' : null,
      fuel_type: mode === 'car' ? 'unknown' : null,
    });
  };

  const handleSubmit = async () => {
    const cleaned = entries
      .filter((e) => {
        if (Number(e.days_per_week) <= 0) return false;
        if (e.distance_source === 'addresses') {
          return e.home_address?.trim() && e.work_address?.trim();
        }
        return Number(e.one_way_distance) > 0;
      })
      .map((e) => ({
        mode: e.mode,
        one_way_distance: e.distance_source === 'addresses' ? null : Number(e.one_way_distance),
        days_per_week: Number(e.days_per_week),
        activity_unit: e.activity_unit,
        vehicle_type: e.vehicle_type,
        size_class: e.size_class,
        fuel_type: e.fuel_type,
        home_address: e.distance_source === 'addresses' ? e.home_address.trim() : null,
        work_address: e.distance_source === 'addresses' ? e.work_address.trim() : null,
      }));
    if (cleaned.length === 0 && !wfh) {
      toast.error('Add at least one commute leg or WFH days');
      return;
    }
    setSubmitting(true);
    try {
      const res = await submitCommuteResponse(token, {
        respondent_name: name || undefined,
        respondent_email: email || undefined,
        department: department || undefined,
        wfh_days_per_week: Number(wfh) || 0,
        entries: cleaned,
      });
      setResult(res);
      toast.success('Thanks — your footprint is recorded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (err) return <CenteredMessage title="Survey unavailable" body={err} />;
  if (!survey) return <CenteredMessage title="Loading…" />;

  if (result) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white p-6 flex items-center justify-center">
        <Card className="max-w-lg w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-emerald-800">
              <Leaf className="w-5 h-5" /> Thanks — submission recorded
            </CardTitle>
            <CardDescription>
              Your annual commute footprint has been added anonymously to your
              company's Scope 3 inventory.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-emerald-900">{Number(result.total_co2e_kg).toFixed(1)} kg CO₂e / year</div>
            <p className="text-sm text-slate-500 mt-2">Calculated from UK DESNZ 2026 GHG conversion factors.</p>
            {result.breakdown?.length > 0 && (
              <ul className="mt-4 text-sm text-slate-700 list-disc pl-5">
                {result.breakdown.map((b, i) => (
                  <li key={i}>{b.mode} — {Number(b.co2e_kg).toFixed(1)} kg CO₂e</li>
                ))}
              </ul>
            )}
            {result.skipped?.length > 0 && (
              <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="font-semibold">
                  {result.skipped.length} part{result.skipped.length > 1 ? 's' : ''} of your commute could not be counted:
                </p>
                <ul className="mt-1 list-disc pl-5">
                  {result.skipped.map((s, i) => (
                    <li key={i}>Entry {s.entry} ({s.mode}): {s.reason}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-3xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Leaf className="w-5 h-5 text-emerald-600" /> {survey.name}
            </CardTitle>
            {survey.description && <CardDescription>{survey.description}</CardDescription>}
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-sm text-slate-600">
              This survey asks about your typical week. Your answers help your employer
              measure Scope 3 Category 7 emissions under the GHG Protocol. Responses
              are anonymous unless you choose to add your name.
            </p>

            <section>
              <h3 className="font-semibold text-slate-900 mb-2">About you (optional)</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
                <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
                <div><Label>Department</Label><Input value={department} onChange={(e) => setDepartment(e.target.value)} /></div>
              </div>
            </section>

            <section>
              <h3 className="font-semibold text-slate-900 mb-2">Working-from-home</h3>
              <div className="flex items-center gap-3">
                <Label className="min-w-fit">WFH days per week</Label>
                <Input type="number" min="0" max="7" step="0.5" value={wfh} onChange={(e) => setWfh(e.target.value)} className="w-24" />
                <span className="text-xs text-slate-500">Covers office equipment + heating (DEFRA homeworking factor).</span>
              </div>
            </section>

            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-slate-900">Commute legs</h3>
                <Button size="sm" variant="outline" onClick={addEntry}><Plus className="w-3 h-3 mr-1" />Add leg</Button>
              </div>
              <div className="space-y-3">
                {entries.map((e) => (
                  <div key={e.id} className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end p-3 rounded-lg border bg-white">
                    <div className="md:col-span-2">
                      <Label>Mode</Label>
                      <Select value={e.mode} onValueChange={(v) => handleModeChange(e.id, v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    {e.mode === 'car' && (
                      <>
                        <div>
                          <Label>Size</Label>
                          <Select value={e.size_class} onValueChange={(v) => updateEntry(e.id, { size_class: v, vehicle_type: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {CAR_SIZES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Fuel</Label>
                          <Select value={e.fuel_type} onValueChange={(v) => updateEntry(e.id, { fuel_type: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {CAR_FUELS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </>
                    )}
                    {e.mode === 'rail' && (
                      <div className="md:col-span-2">
                        <Label>Rail type</Label>
                        <Select value={e.vehicle_type} onValueChange={(v) => updateEntry(e.id, { vehicle_type: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {RAIL_TYPES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {e.mode === 'bus' && (
                      <div className="md:col-span-2">
                        <Label>Bus type</Label>
                        <Select value={e.vehicle_type} onValueChange={(v) => updateEntry(e.id, { vehicle_type: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {BUS_TYPES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {e.mode === 'ferry' && (
                      <div className="md:col-span-2">
                        <Label>Ferry</Label>
                        <Select value={e.vehicle_type} onValueChange={(v) => updateEntry(e.id, { vehicle_type: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {FERRY_TYPES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div className="md:col-span-2">
                      <Label>Distance source</Label>
                      <Select value={e.distance_source} onValueChange={(v) => updateEntry(e.id, { distance_source: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="manual">Enter one-way distance</SelectItem>
                          <SelectItem value="addresses">Let the app calculate from home + work addresses</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {e.distance_source === 'addresses' ? (
                      <>
                        <div className="md:col-span-2">
                          <Label>Home address</Label>
                          <Input value={e.home_address} onChange={(ev) => updateEntry(e.id, { home_address: ev.target.value })} placeholder="e.g. 12 Oak Street, London" />
                        </div>
                        <div className="md:col-span-2">
                          <Label>Work address</Label>
                          <Input value={e.work_address} onChange={(ev) => updateEntry(e.id, { work_address: ev.target.value })} placeholder="e.g. Canary Wharf, London" />
                        </div>
                      </>
                    ) : (
                      <div>
                        <Label>One-way dist. ({e.activity_unit})</Label>
                        <Input type="number" step="any" value={e.one_way_distance} onChange={(ev) => updateEntry(e.id, { one_way_distance: ev.target.value })} />
                      </div>
                    )}
                    <div>
                      <Label>Days / week</Label>
                      <Input type="number" min="0" max="7" step="0.5" value={e.days_per_week} onChange={(ev) => updateEntry(e.id, { days_per_week: ev.target.value })} />
                    </div>

                    <div className="flex justify-end">
                      {entries.length > 1 && (
                        <Button size="icon" variant="ghost" onClick={() => removeEntry(e.id)}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <Button onClick={handleSubmit} disabled={submitting} className="w-full" size="lg">
              {submitting ? 'Submitting…' : 'Submit my commute'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function CenteredMessage({ title, body }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <Card className="max-w-md w-full">
        <CardHeader><CardTitle>{title}</CardTitle></CardHeader>
        {body && <CardContent><p className="text-slate-600">{body}</p></CardContent>}
      </Card>
    </div>
  );
}
